<script setup lang="ts">
import { onMounted, ref, useTemplateRef, watch } from 'vue';

import type { Employee } from '@/types/employee';
import { useRoute } from 'vue-router';

import CustomButton from '@/components/CustomButton.vue';
import ChangeEmailDialog from '@/components/dialogs/ChangeEmailDialog.vue';
import ChangePasswordDialog from '@/components/dialogs/ChangePasswordDialog.vue';
import ConfirmDialog from '@/components/dialogs/ConfirmDialog.vue';
import TextField from '@/components/forms/TextField.vue';
import InformationBox from '@/components/InformationBox.vue';
import { AuthRole } from '@/enums/roles';
import { useAuthStore } from '@/stores/auth';
import { useEmployeeStore } from '@/stores/employee';
import { useGlobalStore } from '@/stores/global';
import { type EmployeeForm } from '@/types/employee';
import { formatDateFieldDate, minimumDate, toDisplayDate } from '@/utils/date';
import toast from '@/utils/toast';
import { dateRange, email, required } from '@/utils/validation';
import { useI18n } from 'vue-i18n';

const authStore = useAuthStore();
const employeeStore = useEmployeeStore();
const globalStore = useGlobalStore();

const route = useRoute();
const { t: translate } = useI18n();

let employeeIdFromUrl = route.params.id as string;

// TODO: guard this route instead of checking if employee exists here.
const employee = ref<Employee | null>();
const isChangePasswordDialogOpen = ref(false);
const isChangeEmailDialogOpen = ref(false);

const isSuperAdminChecked = ref(false);
const isViewingOwnProfile = ref(false);
const isConfirmDialogVisible = ref(false);
const isInitializing = ref(true);
const isLoading = ref(false);
const form = useTemplateRef('form');

const dateOfBirthMax = formatDateFieldDate(new Date());

if (!employeeIdFromUrl) toast.error('EmployeeNotFound');

function setViewingOwnProfile(): void {
    if (authStore.isAuthenticated && employee.value) {
        isViewingOwnProfile.value = authStore.getLoggedInEmployee()._id === employee.value._id;
        form.value?.resetValidation();
    }
}

async function fetchProfileEmployeeAndLoggedInEmployee(): Promise<void> {
    await loadEmployee(employeeIdFromUrl);

    setViewingOwnProfile();
}

onMounted(async () => {
    await fetchProfileEmployeeAndLoggedInEmployee();
    isInitializing.value = false;
});

watch(
    () => route.params.id,
    async (newEmployeeId) => {
        employeeIdFromUrl = Array.isArray(newEmployeeId) ? newEmployeeId[0] : newEmployeeId;
        await fetchProfileEmployeeAndLoggedInEmployee();
    }
);

async function handleUpdateEmployee(employee: Employee): Promise<void> {
    const validationResult = await form.value?.validate();
    if (!validationResult?.valid) return;

    isLoading.value = true;
    try {
        await employeeStore.updateEmployee(employee as EmployeeForm);
        await fetchProfileEmployeeAndLoggedInEmployee();
    } finally {
        isLoading.value = false;
    }
}

async function restoreUpdateEmployeeForm(): Promise<void> {
    await fetchProfileEmployeeAndLoggedInEmployee();
    toast.success('OriginalValuesRestored');
}

async function loadEmployee(employeeId: string): Promise<void> {
    employee.value = await employeeStore.fetchById(employeeId);

    if (authStore.isSuperAdmin()) {
        isSuperAdminChecked.value = employee.value?.isSuperAdmin === true;
    }
}

/**
 * (un)check the checkbox and updates the employee's super admin role
 */
async function updateEmployeeSuperAdminRole(): Promise<void> {
    if (!employee.value) return;

    isSuperAdminChecked.value = !isSuperAdminChecked.value;
    employee.value.isSuperAdmin = isSuperAdminChecked.value;

    await employeeStore.updateEmployee(employee.value as EmployeeForm);
}

async function loginAs(): Promise<void> {
    if (!employee.value) return;
    await authStore.login(employee.value.email);
}
</script>

<template>
    <v-fade-transition>
        <div v-show="!isInitializing && !globalStore.isLoading">
            <p v-if="!employee">Geen medewerker gevonden</p>

            <v-row v-else>
                <v-col lg="7" md="10" cols="12">
                    <template v-if="!isViewingOwnProfile">
                        <InformationBox
                            :fields="[
                                { title: 'Voorletters', value: employee.initials },
                                { title: 'Voornaam', value: employee.firstName },
                                { title: 'Achternaam', value: employee.lastName },
                                { title: 'Geboortedatum', value: toDisplayDate(employee.dateOfBirth) },
                                { title: 'Personeelsnummer', value: employee.staffNumber ?? '-' },
                                { title: 'Functie', value: employee.jobTitle ?? '-' },
                                { title: 'Email', value: employee.email }
                            ]"
                        />
                        <div v-if="authStore.isSuperAdmin()" class="mt-10">
                            <v-row class="ga-10 align-center mb-4">
                                <v-col md="3" cols="5">
                                    <p class="font-weight-bold">{{ translate(`enums.AuthRole.${AuthRole.SuperAdmin}`) }}</p>
                                </v-col>
                                <v-checkbox
                                    v-model="isSuperAdminChecked"
                                    @click.prevent="isConfirmDialogVisible = true"
                                    density="compact"
                                    hide-details
                                />
                            </v-row>
                            <CustomButton text="Inloggen als" @click="loginAs" />
                        </div>
                    </template>

                    <template v-else>
                        <h3>Persoonlijke gegevens</h3>
                        <v-form ref="form">
                            <TextField label="Voorletters" v-model="employee.initials" required :rules="[required]" />
                            <TextField label="Voornaam" v-model="employee.firstName" required :rules="[required]" />
                            <TextField label="Achternaam" v-model="employee.lastName" required :rules="[required]" />
                            <TextField label="Personeelsnummer" v-model="employee.staffNumber" />
                            <TextField label="Functie" v-model="employee.jobTitle" readonly />
                            <TextField
                                label="Geboortedatum"
                                v-model="employee.dateOfBirth"
                                type="date"
                                :min="minimumDate"
                                :max="dateOfBirthMax"
                                :rules="[dateRange(minimumDate, dateOfBirthMax)]"
                            />
                            <v-row class="d-flex justify-end ga-5 mt-7">
                                <v-tooltip
                                    location="bottom"
                                    text="Klik hier om alle formuliervelden naar het origineel te zetten"
                                    v-slot:activator="{ props }"
                                >
                                    <CustomButton v-bind="props" color="alt" @click="restoreUpdateEmployeeForm" text="Formulier herstellen" />
                                </v-tooltip>

                                <CustomButton @click="handleUpdateEmployee(employee)" text="Wijzigen" :loading="isLoading" />
                            </v-row>
                        </v-form>
                        <div v-if="isViewingOwnProfile" class="mt-15">
                            <h3>Logingegevens</h3>
                            <TextField
                                class="mb-2"
                                label="E-mailadres"
                                v-model="employee.email"
                                :isReadonly="true"
                                :rules="[email, required]"
                                required
                            />

                            <div class="d-flex align-start mt-5 ga-5">
                                <CustomButton @click="isChangeEmailDialogOpen = true" text="E-mailadres wijzigen" />
                                <CustomButton @click="isChangePasswordDialogOpen = true" text="Wachtwoord wijzigen" />
                            </div>
                        </div>
                    </template>
                </v-col>
            </v-row>
            <ConfirmDialog
                v-model="isConfirmDialogVisible"
                @continue="updateEmployeeSuperAdminRole()"
                @cancel="isConfirmDialogVisible = false"
                :text="`Wil je hoofdbeheerder rechten ${!isSuperAdminChecked ? 'toekennen aan' : 'verwijderen van'} de medewerker ${employee?.fullName}?`"
            />
            <ChangePasswordDialog v-model="isChangePasswordDialogOpen" />
            <ChangeEmailDialog v-model="isChangeEmailDialogOpen" @closeDialog="isChangeEmailDialogOpen = false" />
        </div>
    </v-fade-transition>
</template>
